import { PrimaryButton, Stack } from "@fluentui/react";
import { Formik, FormikErrors } from "formik";
import * as React from "react";
import { Loading } from "../../../services/Loading";
import { IPreviousMeeting, IPreviousMeetingEmail } from "../../../vm";
import { childrenGap, FILE_UPLOAD_CONFIG, FILE_UPLOAD_MODULES } from "../../common/Constants";
import CustomDrawer from "../../common/CustomDrawer";
import { ToastContext } from "../../common/ToastProvider";
import {
    DefaultButton,
    IStackTokens,
    // TagPicker,
    // Text,
    TextField,
} from "office-ui-fabric-react";
import * as yup from "yup";
import {
    sendPreviousMeetingEmail,
} from "../../../services/customers/CustomerService";
import { getFileTypeErrorMessage, validateFileType } from "../../../services/UtilService";

const schema = yup.object().shape({
    meetingIds: yup.array().required("at least one should be there"),
    mailSubject: yup.string().required("required"),
    mailBody: yup.string().required("required"),
    // agenda: yup.string().required("required"),
    to: yup.string().required("required"),
    cc: yup.string().optional(),
    file: yup.mixed().optional(),
    fileName: yup.string().optional(),
});

const wrapStackTokens: IStackTokens = { childrenGap: 16 };

export interface ManagePmSendEmailDialogProps {
    handleClose: Function;
    meetingIds: string[];
    meetingList: IPreviousMeeting[];
}

const ManagePmSendEmailDialog: React.FC<ManagePmSendEmailDialogProps> = ({
    handleClose,
    meetingIds,
    meetingList
}) => {
    //custom hooks
    const { showToast } = React.useContext(ToastContext);

    // file upload config
    const fileUploadConfig = FILE_UPLOAD_CONFIG[FILE_UPLOAD_MODULES.MANAGE_PM_SEND_EMAIL];

    // local state
    const [isLoading, setLoading] = React.useState(false);
    const [previousMeetingEmail, setPreviousMeetingEmail] = React.useState({
        meetingIds,
        agenda: "",
        mailBody: "Hi\n\nThank you all for participating and contributing to Review Meeting. I appreciate the time and effort each of you put into preparing for the meeting and the valuable insights you shared.\n\nPlease find the attached MOM along with action points and timeline.\n\nIf you have any questions or need further clarification, would appreciate you to append the MOM if any point has been missed.\n\nNote: Please feel free to connect with your STTGDC Service Manager as this is an auto- generated email.\n\nRegards,\nSTTGDC",
        mailSubject: "",
        to: "",
        cc: "",
        file: undefined,
        fileName: "",
    } as IPreviousMeetingEmail);

    // hooks
    React.useEffect(() => {
        let dict: { [key: string]: string } = {};
        meetingList.filter((d) => meetingIds.includes(d._id as string)).forEach((d) => {
            d.sttParticipantEmail.split(",").forEach((pEmail) => {
                if (dict[pEmail.toLowerCase()] == null) dict[pEmail.toLowerCase()] = pEmail;
            });
            d.customerParticipantEmail.split(",").forEach((cEmail) => {
                if (dict[cEmail.toLowerCase()] == null) dict[cEmail.toLowerCase()] = cEmail;
            });

            // action items
            d.actionItems.forEach((dd) => {
                dd.ownerEmail.split(",").forEach((oEmail) => {
                    if (dict[oEmail.toLowerCase()] == null) dict[oEmail.toLowerCase()] = oEmail;
                });
            })
        });
        setPreviousMeetingEmail({
            ...previousMeetingEmail,
            meetingIds,
            to: Object.keys(dict).join(",")
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDialogClose = (mailSend?: boolean) => {
        handleClose(mailSend);
    };

    return (
        <React.Fragment>
            {isLoading && <Loading />}
            <CustomDrawer
                title={"Send Customer Meeting Email to Customer"}
                handelOpen={true}
                onClose={() => handleDialogClose()}
            >
                <Stack tokens={childrenGap}>
                    <Formik
                        enableReinitialize
                        validationSchema={schema}
                        initialValues={previousMeetingEmail}
                        validate={(values: IPreviousMeetingEmail) => {
                            const errors: FormikErrors<IPreviousMeetingEmail> = {}
                            if (values.file) {
                                const isValidType = validateFileType(
                                    values.file,
                                    fileUploadConfig.acceptedFileTypes
                                );

                                if (!isValidType)
                                errors.file = getFileTypeErrorMessage(
                                    fileUploadConfig.fileTypeDescription
                                );
                            }
                            return errors
                        }}
                        onSubmit={async (
                            values: IPreviousMeetingEmail,
                            { setSubmitting }
                        ) => {
                            setLoading(true);
                            let obj = { ...values };
                            const result = await sendPreviousMeetingEmail(obj);
                            if (result?.success) {
                                setLoading(false);
                                showToast("Customer will receive email shortly", "success");
                                handleDialogClose(true);
                            } else {
                                showToast(
                                    result?.message || "Error while sending customer meeting email to customer",
                                    "error"
                                );
                            }
                            setLoading(false);
                            setSubmitting(false);
                        }}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                            setFieldValue,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <Stack wrap tokens={wrapStackTokens}>
                                    <Stack.Item className="custom-textfield">
                                        <TextField
                                            name="to"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.to}
                                            label="To"
                                            placeholder="type multiple emails separated by comma (,)"
                                            errorMessage={errors.to && touched.to ? errors.to : ""}
                                        />
                                    </Stack.Item>
                                    <Stack.Item className="custom-textfield">
                                        <TextField
                                            name="cc"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.cc}
                                            label="CC"
                                            placeholder="type multiple emails separated by comma (,)"
                                            errorMessage={errors.cc && touched.cc ? errors.cc : ""}
                                        />
                                    </Stack.Item>
                                    <Stack.Item className="custom-textfield">
                                        <TextField
                                            name="mailSubject"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.mailSubject}
                                            label="Email Subject"
                                            errorMessage={errors.mailSubject && touched.mailSubject ? errors.mailSubject : ""}
                                        />
                                    </Stack.Item>
                                    <Stack.Item className="custom-multiline-textfield mb-30">
                                        <TextField
                                            name="mailBody"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.mailBody}
                                            label="Email Body / Content"
                                            errorMessage={errors.mailBody && touched.mailBody ? errors.mailBody : ""}
                                            multiline
                                            autoAdjustHeight
                                        />
                                    </Stack.Item>
                                    <Stack.Item className="custom-textfield mb-30">
                                        <TextField
                                            name="file"
                                            type="file"
                                            accept={fileUploadConfig.acceptedFileTypes}
                                            onChange={(event: any) => {
                                                const files = event.target.files
                                                if (files && files.length > 0) {
                                                    const file = files[0];
                                                    // Validate file type
                                                    const isValidType = validateFileType(
                                                        file,
                                                        fileUploadConfig.acceptedFileTypes
                                                    );

                                                    if (!isValidType) {
                                                        showToast(
                                                        getFileTypeErrorMessage(fileUploadConfig.fileTypeDescription),
                                                        "error"
                                                        );
                                                    }
                                                    setFieldValue("file", file);
                                                    setFieldValue("fileName", file.name);
                                                } else {
                                                    showToast("Select only valid file", "error");
                                                }
                                            }}
                                            onBlur={handleBlur}
                                            label="Attachments (If any)"
                                            errorMessage={
                                                errors.file && touched.file
                                                    ? (errors.file as string)
                                                    : ""
                                            }
                                            description={fileUploadConfig.hintText}
                                            className="file-selector"
                                        />
                                    </Stack.Item>
                                    {/* <Stack.Item className="custom-multiline-textfield mb-30">
                                        <TextField
                                            name="agenda"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.agenda}
                                            label="Agenda / Objective"
                                            errorMessage={errors.agenda && touched.agenda ? errors.agenda : ""}
                                            multiline
                                            autoAdjustHeight
                                        />
                                    </Stack.Item> */}
                                </Stack>
                                <Stack.Item>
                                    <Stack
                                        horizontal
                                        wrap
                                        tokens={wrapStackTokens}
                                        horizontalAlign="end"
                                    >
                                        <DefaultButton
                                            text="Cancel"
                                            onClick={() => handleDialogClose()}
                                            className="borderless-btn"
                                        />
                                        <PrimaryButton type="submit" text={"Send Email"} />
                                    </Stack>
                                </Stack.Item>
                            </form>
                        )}
                    </Formik>
                </Stack>
            </CustomDrawer>
        </React.Fragment>
    );
};

export default ManagePmSendEmailDialog;
